import {
    Box,
    Container,
    Card,
    CardHeader,
    CardBody,
    Heading,
    VStack,
    HStack,
    Text,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Divider,
    List,
    ListItem,
    ListIcon,
    useColorMode,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Code,
    Alert,
    AlertIcon,
    Link,
    Button,
    Icon,
} from "@chakra-ui/react";
import { CheckCircleIcon, TimeIcon, EmailIcon, PhoneIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { FaCoins, FaUsers } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const AdminPage = () => {
    const { colorMode } = useColorMode();
    const navigate = useNavigate();
    const bg = getColor("background.main", colorMode);
    const cardGradientBg =
        colorMode === "light"
            ? "rgba(255,255,255,0.95)"
            : "rgba(20,20,20,0.8)";
    const cardBorder =
        colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
    const textPrimary = colorMode === "light" ? "gray.800" : "white";

    return (
        <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <Container maxW="container.xl" py={12} flex="1">
                <VStack spacing={8} align="stretch">

                    {/* Header */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader>
                            <HStack justify="space-between" flexWrap="wrap" gap={4}>
                                <Box>
                                    <Heading size="xl" color={textPrimary}>Webinar Campaign Planning</Heading>
                                    <Text mt={2} color="gray.500">Business Builder - Monthly Webinar System</Text>
                                </Box>
                                <Button
                                    leftIcon={<Icon as={FaUsers} />}
                                    colorScheme="purple"
                                    onClick={() => navigate("/live-event/registrations")}
                                >
                                    View Registrations
                                </Button>
                            </HStack>
                        </CardHeader>
                        <CardBody>
                            <Alert status="info" mb={4}>
                                <AlertIcon />
                                <Text>
                                    This admin page outlines the complete webinar funnel including SMS/Email campaigns,
                                    landing pages, BB Token rewards, and the purchase flow. Use this as your campaign checklist.
                                </Text>
                            </Alert>

                            {/* Token Reward Summary */}
                            <Alert status="success" borderRadius="md">
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="bold"><Icon as={FaCoins} color="#F59E0B" mr={1} /> BB Token Rewards Per Webinar</Text>
                                    <Text fontSize="sm">
                                        Register: +1 token | Show up live: +1 | Stay till end: +1 | Refer someone: +1 (max 3) | Social post: +1 |
                                        Max per attendee: 5 tokens (AU$250) + 3 referral tokens (AU$150)
                                    </Text>
                                </Box>
                            </Alert>
                        </CardBody>
                    </Card>

                    {/* Campaign Timeline Overview */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader>
                            <Heading size="lg" color={textPrimary}>Campaign Timeline</Heading>
                            <Text mt={2} color="gray.500" fontSize="sm">
                                8-Day Campaign Cycle (repeats monthly)
                            </Text>
                        </CardHeader>
                        <CardBody>
                            <Alert status="success" mb={4} borderRadius="md">
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="bold">Monthly Webinar Revenue Model (Moderate Scenario)</Text>
                                    <Text fontSize="sm">75 registrations | 40% show-up (30 people) | 15% conversion (5 new subs) | $485/mo new MRR + $497 one-time</Text>
                                </Box>
                            </Alert>
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Day</Th>
                                        <Th>Action</Th>
                                        <Th>Channel</Th>
                                        <Th>Destination</Th>
                                        <Th>Status</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr>
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Badge colorScheme="blue">Day 1</Badge>
                                                <Text fontSize="xs" color="gray.500">Teaser</Text>
                                            </VStack>
                                        </Td>
                                        <Td>Interest Teaser - "Something exciting is coming"</Td>
                                        <Td>SMS + Email</Td>
                                        <Td>
                                            <Link href="/live-event/interest" isExternal color="blue.500">
                                                Interest Page <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        </Td>
                                        <Td><Badge colorScheme="yellow">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Badge colorScheme="purple">Day 3</Badge>
                                                <Text fontSize="xs" color="gray.500">Registration Opens</Text>
                                            </VStack>
                                        </Td>
                                        <Td>Registration opens - "Earn 1 BB Token for registering"</Td>
                                        <Td>SMS + Email</Td>
                                        <Td>
                                            <Link href="/live-event/register" isExternal color="blue.500">
                                                Registration Page <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        </Td>
                                        <Td><Badge colorScheme="yellow">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Badge colorScheme="orange">Day 7</Badge>
                                                <Text fontSize="xs" color="gray.500">Reminder</Text>
                                            </VStack>
                                        </Td>
                                        <Td>Day-before reminder</Td>
                                        <Td>SMS + Email</Td>
                                        <Td>-</Td>
                                        <Td><Badge colorScheme="yellow">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Badge colorScheme="red">Day 8</Badge>
                                                <Text fontSize="xs" color="gray.500">LIVE</Text>
                                            </VStack>
                                        </Td>
                                        <Td>Live webinar day (1-hour-before SMS + live start email)</Td>
                                        <Td>SMS + Email</Td>
                                        <Td>
                                            <Link href="/live-event/live" isExternal color="blue.500">
                                                Live Event Page <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        </Td>
                                        <Td><Badge colorScheme="yellow">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Badge colorScheme="green">Day 8</Badge>
                                                <Text fontSize="xs" color="gray.500">+60 min</Text>
                                            </VStack>
                                        </Td>
                                        <Td>Purchase offer email (scarcity: limited premium spots)</Td>
                                        <Td>In-Event + Email</Td>
                                        <Td>
                                            <Link href="/live-event/purchase" isExternal color="blue.500">
                                                Purchase Page <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        </Td>
                                        <Td><Badge colorScheme="yellow">Pending</Badge></Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    {/* SMS & Email Campaign Details */}
                    <Accordion allowMultiple defaultIndex={[0, 1, 2, 3]}>

                        {/* Day 1 - Interest/Teaser */}
                        <AccordionItem>
                            <AccordionButton bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px" borderRadius="md" mb={2}>
                                <Box flex="1" textAlign="left">
                                    <HStack>
                                        <Badge colorScheme="blue" fontSize="md" px={3} py={1}>Day 1</Badge>
                                        <Heading size="md">Teaser - Interest Page</Heading>
                                    </HStack>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                <VStack spacing={6} align="stretch">
                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <PhoneIcon color="green.500" />
                                                <Heading size="sm">SMS Template</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`Hi [First Name], Tom Miller here. Something exciting is coming - a free live session on taking back control of your business data and building true sovereignty. Interested? [LINK]

Reply STOP to unsubscribe`}
                                            </Code>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <EmailIcon color="blue.500" />
                                                <Heading size="sm">Email Template</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>Subject:</strong> Something exciting is coming, [First Name]</Text>
                                                <Divider />
                                                <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`Hi [First Name],

I've been working on something I'm really excited about, and I want to give you first access.

I'm hosting a FREE live webinar on Business Sovereignty - how to own your data, train AI on YOUR terms, and stop building someone else's empire.

This isn't theory. I'll show you the actual platform I've built and how it changes everything about running a business in the digital age.

Plus, just for registering, you'll earn a BB Token worth AU$50 toward your subscription.

Details coming soon. Drop your email to be first in line.

[BUTTON: I'm Interested]
Link: /live-event/interest

Tom Miller
Tom Miller Services`}
                                                </Code>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <Heading size="sm">Interest Page Content</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>URL:</strong> <Link href="/live-event/interest" isExternal color="blue.500">/live-event/interest <ExternalLinkIcon mx="2px" /></Link></Text>
                                                <Divider />
                                                <List spacing={2}>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Headline: "Something exciting is coming"</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Subheadline: Free live webinar on business sovereignty</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />BB Token incentive: "Earn AU$50 just for registering"</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Email capture form</ListItem>
                                                </List>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>

                        {/* Day 3 - Registration */}
                        <AccordionItem>
                            <AccordionButton bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px" borderRadius="md" mb={2}>
                                <Box flex="1" textAlign="left">
                                    <HStack>
                                        <Badge colorScheme="purple" fontSize="md" px={3} py={1}>Day 3</Badge>
                                        <Heading size="md">Registration Opens</Heading>
                                    </HStack>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                <VStack spacing={6} align="stretch">
                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <PhoneIcon color="green.500" />
                                                <Heading size="sm">SMS Template</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`Hi [First Name], registration is now OPEN for the free Business Sovereignty webinar. Register now and earn a BB Token (AU$50). Spots limited. [LINK]

Reply STOP to unsubscribe`}
                                            </Code>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <EmailIcon color="blue.500" />
                                                <Heading size="sm">Email Template</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>Subject:</strong> [First Name], your spot is waiting</Text>
                                                <Divider />
                                                <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`Hi [First Name],

Registration is now open for the free live webinar:

BUSINESS SOVEREIGNTY: Own Your Data, Train Your AI, Scale Your Way
[DATE] at [TIME] AEST
Online via Zoom

In this session, you'll discover:
- Why your current SaaS is making you dependent
- How to train AI on YOUR business data (not theirs)
- The BB Token reward system that pays you to engage
- Live demo of the Business Builder platform

Register now and instantly earn 1 BB Token (AU$50 value).
Attend live? That's another token. Stay till the end? Another one.
You could earn AU$250+ in a single webinar.

[BUTTON: Register Now - It's Free]
Link: /live-event/register

Limited spots. See you there.

Tom Miller
Tom Miller Services`}
                                                </Code>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <Heading size="sm">Registration Page Content</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>URL:</strong> <Link href="/live-event/register" isExternal color="blue.500">/live-event/register <ExternalLinkIcon mx="2px" /></Link></Text>
                                                <Divider />
                                                <List spacing={2}>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Two-column layout: info left, registration form right</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Webinar highlights with checkmarks</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />BB Token incentive card (AU$50 for registering)</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Form: First Name, Last Name, Email, Phone</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Referral tracking via ?ref= query param</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />UTM tracking for campaign attribution</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Auto-issues BB Token on registration (backend)</ListItem>
                                                </List>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>

                        {/* Day 7-8 - Live Event */}
                        <AccordionItem>
                            <AccordionButton bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px" borderRadius="md" mb={2}>
                                <Box flex="1" textAlign="left">
                                    <HStack>
                                        <Badge colorScheme="red" fontSize="md" px={3} py={1}>Day 7-8</Badge>
                                        <Heading size="md">Reminders + Live Event Day</Heading>
                                    </HStack>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                <VStack spacing={6} align="stretch">
                                    <Card variant="outline" borderColor="orange.300">
                                        <CardHeader bg={colorMode === "light" ? "orange.50" : "orange.900"}>
                                            <HStack>
                                                <TimeIcon color="orange.500" />
                                                <Heading size="sm">Day Before Reminder (Day 7)</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={4}>
                                                <Box>
                                                    <Text fontWeight="bold" mb={2}>SMS:</Text>
                                                    <Code p={3} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`[First Name], TOMORROW is the Business Sovereignty webinar! [TIME] AEST via Zoom. Don't miss out - attend live to earn extra BB Tokens. See you there!`}
                                                    </Code>
                                                </Box>
                                                <Box>
                                                    <Text fontWeight="bold" mb={2}>Email Subject:</Text>
                                                    <Text>Tomorrow: Your business changes, [First Name]</Text>
                                                </Box>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <PhoneIcon color="green.500" />
                                                <Heading size="sm">Event Day SMS (1 hour before)</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`[First Name], we're going LIVE in 1 hour! Join now: [ZOOM LINK]

Attend live = +1 BB Token (AU$50). Stay till the end = another one!`}
                                            </Code>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <EmailIcon color="blue.500" />
                                                <Heading size="sm">Event Day Email</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>Subject:</strong> We're LIVE - Join Now [First Name]!</Text>
                                                <Divider />
                                                <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`Hi [First Name],

It's happening NOW!

Click below to join the live webinar:

[BUTTON: Join Live Session Now]
Link: [ZOOM LINK]

Remember: Attend live = +1 BB Token, stay till the end = another one.
That's AU$100 in rewards just for being there!

See you inside!

Tom`}
                                                </Code>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <Heading size="sm">Live Event Page</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>URL:</strong> <Link href="/live-event/live" isExternal color="blue.500">/live-event/live <ExternalLinkIcon mx="2px" /></Link></Text>
                                                <Divider />
                                                <List spacing={2}>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />LIVE NOW badge with pulse animation</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Join on Zoom button (prominent CTA)</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />BB Token earning reminder card</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Support contact info</ListItem>
                                                </List>
                                                <Alert status="warning" mt={2}>
                                                    <AlertIcon />
                                                    <Text>Zoom link needs to be configured before each event</Text>
                                                </Alert>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>

                        {/* Post-Event - Purchase */}
                        <AccordionItem>
                            <AccordionButton bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px" borderRadius="md" mb={2}>
                                <Box flex="1" textAlign="left">
                                    <HStack>
                                        <Badge colorScheme="green" fontSize="md" px={3} py={1}>+60 Min</Badge>
                                        <Heading size="md">Post-Event Purchase Offer</Heading>
                                    </HStack>
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                <VStack spacing={6} align="stretch">
                                    <Card variant="outline" borderColor="green.400" borderWidth="2px">
                                        <CardHeader bg={colorMode === "light" ? "green.50" : "green.900"}>
                                            <Heading size="md" color="green.500">Product Offer Details</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={4}>
                                                <HStack justify="space-between">
                                                    <Text fontWeight="bold">Foundation Plan:</Text>
                                                    <Text fontSize="xl" fontWeight="bold" color="green.500">$97/month</Text>
                                                </HStack>
                                                <HStack justify="space-between">
                                                    <Text fontWeight="bold">Premium Setup (one-time):</Text>
                                                    <Text fontSize="xl" fontWeight="bold" color="purple.500">$497 + $97/mo</Text>
                                                </HStack>
                                                <HStack justify="space-between">
                                                    <Text fontWeight="bold">Free Winner (10 months free):</Text>
                                                    <Text fontSize="md" color="gray.500">1 lucky attendee per webinar</Text>
                                                </HStack>
                                                <Divider />
                                                <Box>
                                                    <Text fontWeight="bold" mb={2}>BB Token Payment Option:</Text>
                                                    <Text fontSize="sm">2 BB Tokens = 1 month free ($97 value). Subscribers also get a 2-token welcome bonus (AU$100).</Text>
                                                </Box>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <HStack>
                                                <EmailIcon color="blue.500" />
                                                <Heading size="sm">Post-Event Email (60 min after)</Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>Subject:</strong> [First Name], your exclusive webinar offer</Text>
                                                <Divider />
                                                <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md">
{`Hi [First Name],

Thank you for joining today's Business Sovereignty webinar!

As promised, here's your exclusive offer:

FOUNDATION PLAN: $97/month
- Full Business Builder platform access
- CRM, Projects, Billing, Calendar
- AI Voice Assistant
- Email & SMS Campaigns
- Monthly BB Token loyalty rewards

PREMIUM SETUP: $497 (one-time) + $97/month
- Everything in Foundation PLUS
- 1-on-1 onboarding call with Tom
- Custom domain & branding setup
- Priority support

ALREADY EARNED BB TOKENS?
You earned tokens today! 2 tokens = 1 month free.
Sign up and get an additional 2-token welcome bonus.

[BUTTON: Subscribe - $97/month]
[BUTTON: Get Premium Setup - $497]

Link: /live-event/purchase

30-day money-back guarantee. Cancel anytime.

Tom Miller
Tom Miller Services`}
                                                </Code>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    <Card variant="outline">
                                        <CardHeader>
                                            <Heading size="sm">Purchase Page Content</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Text><strong>URL:</strong> <Link href="/live-event/purchase" isExternal color="blue.500">/live-event/purchase <ExternalLinkIcon mx="2px" /></Link></Text>
                                                <Divider />
                                                <List spacing={2}>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Two pricing cards: Foundation $97/mo + Premium Setup $497</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Full feature list with checkmarks</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />"Pay with BB Tokens" option + balance check CTA</ListItem>
                                                    <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />30-day money-back guarantee</ListItem>
                                                </List>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>

                    </Accordion>

                    {/* Page Links */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader>
                            <Heading size="md" color={textPrimary}>Page Links</Heading>
                        </CardHeader>
                        <CardBody>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Page</Th>
                                        <Th>URL</Th>
                                        <Th>Status</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr>
                                        <Td>Interest / Teaser Page</Td>
                                        <Td><Link href="/live-event/interest" isExternal color="blue.500">/live-event/interest <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Registration Page</Td>
                                        <Td><Link href="/live-event/register" isExternal color="blue.500">/live-event/register <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Live Event Page</Td>
                                        <Td><Link href="/live-event/live" isExternal color="blue.500">/live-event/live <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Purchase Page</Td>
                                        <Td><Link href="/live-event/purchase" isExternal color="blue.500">/live-event/purchase <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Registrations Admin</Td>
                                        <Td><Link href="/live-event/registrations" isExternal color="blue.500">/live-event/registrations <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>BB Rewards Dashboard</Td>
                                        <Td><Link href="/businessbuilder/rewards" isExternal color="blue.500">/businessbuilder/rewards <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Campaign Planning (this page)</Td>
                                        <Td><Link href="/live-event/admin" isExternal color="blue.500">/live-event/admin <ExternalLinkIcon mx="2px" /></Link></Td>
                                        <Td><Badge colorScheme="green">Complete</Badge></Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </CardBody>
                    </Card>

                    {/* Next Steps Checklist */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader>
                            <Heading size="lg" color={textPrimary}>Next Steps Checklist</Heading>
                            <Text mt={2} color="gray.500" fontSize="sm">
                                Tasks to complete before first webinar launch
                            </Text>
                        </CardHeader>
                        <CardBody>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Task</Th>
                                        <Th>Owner</Th>
                                        <Th>Status</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr>
                                        <Td>Record 20-min practice pitch for review</Td>
                                        <Td>Tom</Td>
                                        <Td><Badge colorScheme="yellow">In Progress</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Configure BB Token for TMS tenant (updateRewardTokenConfig mutation)</Td>
                                        <Td>Dev</Td>
                                        <Td><Badge colorScheme="yellow">In Progress</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Run yarn generate on frontend after backend schema changes</Td>
                                        <Td>Dev</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Set up Zoom meeting link for first webinar</Td>
                                        <Td>Tom</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Configure SMS campaign (Cellcast/Twilio) for Day 1 teaser</Td>
                                        <Td>Dev</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Configure email campaign (Postmark) for Day 1 teaser</Td>
                                        <Td>Dev</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Set webinar date and update event config</Td>
                                        <Td>Tom</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Connect Stripe for subscription payment ($97/mo)</Td>
                                        <Td>Dev</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Test full funnel: Interest → Register → Live → Purchase</Td>
                                        <Td>Tom + Dev</Td>
                                        <Td><Badge colorScheme="gray">Pending</Badge></Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </CardBody>
                    </Card>

                </VStack>
            </Container>
            <FooterWithFourColumns />
        </Box>
    );
};

export default AdminPage;
